import type { App } from 'vue'
import { sensorsInit } from '@fs/utils/dist/lib/sensors/index.js'

export type AnalyticsProps = Record<string, unknown>

/**
 * 真实上报函数。可插拔：在应用入口通过 registerAnalytics 注入
 * （对齐 @repo/http 的 registerToast/registerLogout 模式）。
 * 本仓库无 @repo/utils 包，直接经 `@fs/utils/dist/lib/sensors` 子路径初始化并 registerAnalytics。
 */
let analyticsInstance: ((event: string, props?: AnalyticsProps) => void) | null = null

/**
 * 注册埋点上报函数（与 registerToast 用法一致，在应用入口调用一次）。
 * 传入 null 可注销。
 */
export function registerAnalytics(
  fn: ((event: string, props?: AnalyticsProps) => void) | null
): void {
  analyticsInstance = typeof fn === 'function' ? fn : null
}

/**
 * 上报一个埋点事件。
 * 已注册真实上报函数则调用（try/catch 包裹，异常不外抛）；
 * 未注册时：dev 环境 console.info 便于调试，prod 环境静默 no-op。
 */
export function track(event: string, props?: AnalyticsProps): void {
  if (typeof analyticsInstance === 'function') {
    try {
      analyticsInstance(event, props)
    } catch (e) {
      console.error('[analytics] track error:', e)
    }
    return
  }
  if (import.meta.env.DEV) {
    console.info('[analytics]', event, props ?? {})
  }
}

/**
 * 初始化神策 SDK，并 registerAnalytics(sensors.track)。
 * 失败只 warn，不抛——与 bootstrap 容错一致。
 */
export function initAnalytics(app?: App): void {
  try {
    const env = String(import.meta.env.VUE_APP_ENV || '')
    const { sensors } = sensorsInit({
      env,
      Vue3: app,
      initOptions: {
        // 非 development 打开 SDK log
        show_log: import.meta.env.VUE_APP_ENV !== 'prod',
        is_track_single_page: false,
        AutoTrack: false,
      },
    })
    if (sensors && typeof sensors.track === 'function') {
      registerAnalytics((event, props) => {
        sensors.track(event, props)
      })
    }
  } catch (error) {
    console.warn('[analytics] initAnalytics failed', error)
  }
}